import { ToolbarDockEntry } from '@blocksuite/affine-components/toolbar';
import type { ColorScheme } from '@blocksuite/affine-model';
import { SignalWatcher } from '@blocksuite/global/lit';
import { ContextProvider } from '@lit/context';
import type { ReadonlySignal } from '@preact/signals-core';
import { property } from 'lit/decorators.js';

import {
  type EdgelessDockPort,
  edgelessToolbarContext,
  type EdgelessToolbarSlots,
  edgelessToolbarSlotsContext,
  edgelessToolbarThemeContext,
} from './context.js';

/** What every entry of one dock shares: the menu port, the resize slot and the canvas's themes. */
export interface EdgelessDockEntryScope {
  readonly port: EdgelessDockPort;
  readonly slots: EdgelessToolbarSlots;
  readonly theme$: ReadonlySignal<ColorScheme>;
}

export const EDGELESS_DOCK_ENTRY = 'edgeless-dock-entry';

/**
 * Hosts one existing canvas dock entry (a quick or senior tool's template) wherever the app's dock
 * puts it: a toolbar dock entry that also provides the contexts the canvas tools consume.
 */
export class EdgelessDockEntry extends SignalWatcher(ToolbarDockEntry) {
  private readonly _port = new ContextProvider(this, {
    context: edgelessToolbarContext,
  });

  private readonly _slots = new ContextProvider(this, {
    context: edgelessToolbarSlotsContext,
  });

  private readonly _theme = new ContextProvider(this, {
    context: edgelessToolbarThemeContext,
  });

  @property({ attribute: false })
  accessor scope: EdgelessDockEntryScope | null = null;

  override willUpdate() {
    const scope = this.scope;
    if (!scope) return;
    this._port.setValue(scope.port);
    this._slots.setValue(scope.slots);
    this._theme.setValue(scope.theme$.value);
  }
}

// Declared beside the class: programs that load the canvas toolbar from its sources (without its
// effects) still type the dock's `createElement` call.
declare global {
  interface HTMLElementTagNameMap {
    [EDGELESS_DOCK_ENTRY]: EdgelessDockEntry;
  }
}
